// TODO: Lodash should no longer be used here
// eslint-disable-next-line no-restricted-imports
import _ from 'lodash';
import store from '../redux/store';

import { EnrollmentStatusEnum, HourEnum } from '../enrollments/enums';
import { ScheduledDayType, UnenrollmentType } from '../enrollments/types';
import { getDisplayNameOrFormatFullName } from '../helpers/utils';
import { InterestedFamilyType } from '../integrations/marketplace/types';

import { ContactStatus, ContactType, StudentType } from '@wonderschool/common-base-types';
import { toDateObject } from '../helpers/dates';
import { CONTACT_NOTES, PARENT_NOTES, RELATIONSHIP_TYPES, STUDENT_FAMILY_CONTACT_FIELDS } from './consts';
import { StudentStatusEnum } from './types';

const randomPictures = [
  'https://www.moxit.com/assets/images/placeholders/profile-01.png',
  'https://www.moxit.com/assets/images/placeholders/profile-02.png',
  'https://www.moxit.com/assets/images/placeholders/profile-03.png',
  'https://www.moxit.com/assets/images/placeholders/profile-04.png',
  'https://www.moxit.com/assets/images/placeholders/profile-05.png',
  'https://www.moxit.com/assets/images/placeholders/profile-06.png',
  'https://www.moxit.com/assets/images/placeholders/profile-07.png',
  'https://www.moxit.com/assets/images/placeholders/profile-08.png',
  'https://www.moxit.com/assets/images/placeholders/profile-09.png',
  'https://www.moxit.com/assets/images/placeholders/profile-10.png',
  'https://www.moxit.com/assets/images/placeholders/profile-11.png',
];

export const getRandomStudentPicture = (): string => {
  return randomPictures[_.random(0, randomPictures.length - 1)] || randomPictures[0];
};

export const getPrimaryLocation = (locations: any[]) => {
  if (!locations?.length) return null;
  return (
    locations.find((location) => location.primary) || locations.find((location) => location.active) || locations[0]
  );
};

export const findLocationIdFromRoom = (student: any) => {
  if (!student?.id) return null;
  const { students, rooms } = store.getState();
  const studentState = students.list?.find((s: any) => s.id === student.id);

  if (!studentState || !studentState.rooms || studentState.rooms.length <= 0) return null;

  const roomId = studentState.rooms[0];
  const roomState = rooms.list.find((r: any) => r.id === roomId);

  if (!roomState) return null;

  return roomState.location;
};

export const findPrimaryLocationId = () => {
  const { locations } = store.getState();
  return getPrimaryLocation(locations?.list || [])?.id ?? null;
};

export const findLocationIdForStudent = (student: any) => findLocationIdFromRoom(student) || findPrimaryLocationId();

export const resolveInvoicePlanLocationId = (
  persistedLocationId: string | null | undefined,
  student: any,
  locations?: any[],
  organizationStudents?: any[],
  rooms?: any[]
) => {
  if (persistedLocationId != null) return persistedLocationId;
  const studentState = organizationStudents?.find((candidate) => candidate.id === student?.id);
  const roomLocationId =
    organizationStudents && rooms
      ? rooms.find((room) => room.id === studentState?.rooms?.[0])?.location
      : findLocationIdFromRoom(student);
  if (roomLocationId) return roomLocationId;
  return locations ? (getPrimaryLocation(locations)?.id ?? null) : findPrimaryLocationId();
};

// Take the original relationship types and munge them into a newer format
// Does NOT mutate the original object
export const transformRelationship = (data: any) => {
  const { relationship = null, relationshipNotes = null, ...rest } = data ?? {};

  // eliminate the 'guardian' relationship
  const newRelationship = relationship === RELATIONSHIP_TYPES.guardian ? RELATIONSHIP_TYPES.contact : relationship;

  return { ...rest, relationship: newRelationship, relationshipNotes };
};

export const getOrCreateRelationshipNotes = (data: any, t: (a: string) => string) => {
  const { relationship = null, relationshipNotes = null } = data ?? {};

  if (relationshipNotes) return relationshipNotes;

  if (relationship === RELATIONSHIP_TYPES.parent) {
    return t(PARENT_NOTES);
  } else {
    return t(CONTACT_NOTES);
  }
};

export const createArrayOfFamilyContacts = (student: any) => {
  if (!student?.family || !Object.keys(student.family).length) return [];

  const contacts = Object.keys(student.family).map((id) => {
    return { id, ...student.family[id] };
  });
  return contacts;
};

/**
 * Resolve the recipient the backend's billing-plan-end email path would actually use,
 * mirroring functions/src/helpers/students.js (getResponsibleForBilling +
 * getParentsWithEmail + contactUtils):
 * - a stored responsibleForBilling is authoritative — the backend returns it as-is and
 *   never falls back to another contact, so if it has no email the send is skipped;
 * - otherwise only family contacts with relationship 'parent' and an email qualify,
 *   preferring a verified parent (status verified + uid), then any non-disabled,
 *   non-deleted parent.
 * Returns undefined when the backend would not send, so the dialog can say so instead
 * of promising an email that will never go out.
 */
export const getBillingContactWithEmail = (student: any) => {
  const parents = createArrayOfFamilyContacts(student).filter(
    (contact) => contact.email && contact.relationship === RELATIONSHIP_TYPES.parent
  );
  const findVerifiedParent = (candidates: any[]) =>
    candidates.find((contact) => contact.status === ContactStatus.verified && Boolean(contact.uid));

  const responsibleForBilling = student?.responsibleForBilling;
  if (responsibleForBilling) {
    // Mirror updateResponsibleForBilling: an invitation-type contact that has since been
    // verified is promoted to the matching verified family parent; otherwise the stored
    // object is returned as-is (and without an email, the backend skips the send).
    if (responsibleForBilling.type !== 'parent') {
      const matchingParent = parents.find(
        (contact) => contact.id === responsibleForBilling.id || contact.uid === responsibleForBilling.uid
      );
      if (matchingParent && findVerifiedParent([matchingParent])) return matchingParent;
    }
    return responsibleForBilling.email ? responsibleForBilling : undefined;
  }

  const verifiedParent = findVerifiedParent(parents);
  if (verifiedParent) return verifiedParent;

  return parents.find(
    (contact) => contact.status !== ContactStatus.disabled && contact.status !== ContactStatus.deleted
  );
};

/**
 * Returns true if student matches searchText. Tries to match with displayName as
 * well as names in families.
 */
export const doesQueryMatchStudent = (searchText: string) => (student: any) => {
  const { displayName, family, allergies } = student;

  const searchTextLower = searchText.toLowerCase();

  if (displayName?.toLowerCase().includes(searchTextLower)) {
    return true;
  }

  if (allergies?.toLowerCase().includes(searchTextLower)) {
    return true;
  }

  if (family) {
    return Object.keys(family).some((key) => {
      const name = getDisplayNameOrFormatFullName(family[key], true).toLowerCase();
      return name.includes(searchTextLower);
    });
  }
  return false;
};

/**
 * Returns the subarray of students that match searchText. Tries to match with displayName as
 * well as names in families.
 */
export const searchStudents = (searchText: string, students: any[]) =>
  students.filter(doesQueryMatchStudent(searchText));

export const computeEnforceAutomaticPayment = (student: any, organization: any) => {
  const studentModifiedDate = student.automaticPayment?.modifiedDate ?? 0;
  const organizationModifiedDate = organization.automaticPayment?.modifiedDate ?? 0;

  return studentModifiedDate > organizationModifiedDate
    ? !!student.automaticPayment?.enforce
    : !!organization.automaticPayment?.enforce;
};

export const createStudentFamilyFromContact = (contact: any) => {
  return STUDENT_FAMILY_CONTACT_FIELDS.reduce((acc: any, key) => {
    if (contact[key]) {
      acc[key] = contact[key];
    }
    return acc;
  }, {});
};

export const getEnrollmentDate = (student: any): Date => {
  return student?.enrollment?.startDate ?? student.enrollmentDate;
};

export const getUnenrollmentData = (student: any): UnenrollmentType | null => {
  if (!student) return null;

  const unenrolled = student.enrollment?.unenrolled;

  return {
    reason: unenrolled?.reason ?? student.unenrollmentReason,
    initiatedBy: unenrolled?.initiatedBy ?? student.unenrollmentInitiatedBy,
    date: unenrolled?.date ?? student.unenrollmentDate,
    notes: unenrolled?.notes ?? student.unenrollmentNotes,
    settlementAdvisory: unenrolled?.settlementAdvisory,
  } as UnenrollmentType;
};

// These are wrappers around the legacy student.enrollmentXXX and student.enrollment.XXX fields
// First check the enrollment map, then the enrollmentXXX fields
export const resolveLegacyStatus = (student: any) => {
  const todaysDate = new Date();
  if (student?.enrollmentStatus) {
    if (todaysDate.valueOf() < student?.enrollmentDate) {
      return EnrollmentStatusEnum.PENDING;
    } else {
      return EnrollmentStatusEnum.ENROLLED;
    }
  } else {
    return EnrollmentStatusEnum.UNENROLLED;
  }
};
export const getEnrollmentStatus = (student: any): EnrollmentStatusEnum => {
  const stored = student?.enrollment?.status ?? resolveLegacyStatus(student);

  const unenrollmentDate = student?.enrollment?.unenrolled?.date;
  if (unenrollmentDate) {
    const unenrollDate = toDateObject(unenrollmentDate);
    if (unenrollDate) {
      const today = new Date();
      // Future date: student is still enrolled regardless of stored status
      if (unenrollDate > today) return EnrollmentStatusEnum.ENROLLED;
      // Past date: student is effectively unenrolled regardless of stored status
      return EnrollmentStatusEnum.UNENROLLED;
    }
  }

  return stored;
};

export const isStudentEnrolled = (student: any): boolean => {
  return getEnrollmentStatus(student) === EnrollmentStatusEnum.ENROLLED;
};
export const isStudentUnenrolled = (student: any): boolean => {
  return getEnrollmentStatus(student) === EnrollmentStatusEnum.UNENROLLED;
};
export const isStudentPending = (student: any): boolean => {
  return getEnrollmentStatus(student) === EnrollmentStatusEnum.PENDING;
};
export const canArchiveStudent = (student: any): boolean => {
  return isStudentUnenrolled(student) || isStudentPending(student);
};

// True once the family has been sent an enrollment packet. Note that a
// contact showing as "verified" does NOT imply this: a parent who already
// held an account at another organization is verified on creation.
export const hasEnrollmentInvitation = (student: any): boolean => {
  return (student?.enrollment?.invitationRecipients ?? []).length > 0;
};

// The enrollment wizard is the only way to send a family their enrollment
// packet, so offer it to anyone who could still need one. `pending` alone is
// not a sufficient gate: a student added with automaticallyEnroll and a start
// date of today or earlier is created `enrolled` with no invitation ever sent
// (see StudentAddPage), and would otherwise have no entry point at all.
export const canSendEnrollmentInvitation = (student: any): boolean => {
  if (!student?.id || isStudentArchived(student)) return false;

  return isStudentPending(student) || isStudentEnrolled(student);
};

// sets the enrollment status and returns a new student object
export const setEnrollmentStatus = (student: any, status: EnrollmentStatusEnum): any => {
  if (!student || !status) throw new Error('setEnrollmentStatus: missing required parameters');

  const legacyStatus: boolean = status === EnrollmentStatusEnum.ENROLLED;
  const newEnrollment = { ...(student?.enrollment ?? {}), status: status };
  const newStudent = { ...student, enrollmentStatus: legacyStatus, enrollment: newEnrollment };
  return newStudent;
};

// Student archive/unarchive
export const isStudentArchived = (student: any): boolean => {
  return student?.status === StudentStatusEnum.ARCHIVED;
};

export const archiveStudent = (student: any): any => {
  if (!student) throw new Error('archiveStudent: missing required parameters');
  if (!canArchiveStudent(student)) throw new Error('archiveStudent: student must be unenrolled or pending');
  return { ...student, status: StudentStatusEnum.ARCHIVED };
};

export const unarchiveStudent = (student: any): any => {
  if (!student) throw new Error('unarchiveStudent: missing required parameters');
  if (!isStudentArchived(student)) throw new Error('unarchiveStudent: student must be archived');
  return { ...student, status: StudentStatusEnum.ACTIVE };
};

export const createScheduledDaysFromInterestedFamily = (interestedFamily: InterestedFamilyType): ScheduledDayType[] => {
  const { desiredSchedule = [], mostRecentApplicationSchedule = [] } = interestedFamily.student ?? {};

  const mpSchedule = desiredSchedule?.length ? desiredSchedule : mostRecentApplicationSchedule;
  return (mpSchedule ?? []).map((day) => {
    return {
      day: day.day,
      startTime: createHourEnumFromString(day.startsAt),
      endTime: createHourEnumFromString(day.endsAt),
    } as ScheduledDayType;
  });
};

export const createHourEnumFromString = (timestamp: string): HourEnum | null => {
  const [hour, minute] = timestamp.split(':');
  const testTime = `${hour}:${minute}`;

  let foundTime: HourEnum | null = null;
  Object.values(HourEnum).forEach((hour) => {
    if (hour <= testTime) {
      foundTime = hour;
    }
  });
  return foundTime;
};

export function getRelatedStudentsByContact(contactId: string, students: StudentType[]): any {
  return students.filter((student) => student.familyMembers?.includes(contactId));
}

// Deleted family contacts stay pickable: adding one to a child turns the parent back on. `+deleted@` marks a contact deleted for good.
export const isPickableFamilyContact = (contact: { type?: string; status?: string; email?: string }): boolean =>
  contact.type === ContactType.FAMILY &&
  contact.status !== ContactStatus.disabled &&
  !(contact.status === ContactStatus.deleted && contact.email?.includes('+deleted@'));
